import { Injectable } from '@angular/core';

type VocalLabsIncomingMessage =
  | {
      event: 'playAudio';
      media?: {
        contentType?: string;
        sampleRate?: number;
        payload?: string;
      };
    }
  | { event: string; [key: string]: any };

type VocalLabsOutgoingMessage =
  | {
      event: 'start';
      start: {
        streamId: 'inbound';
        mediaFormat: { Encoding: 'audio/x-l16'; sampleRate: 8000 };
      };
    }
  | { event: 'hangup_source'; source: 'in_progress' }
  | { event: 'media'; media: { contentType: 'audio/x-l16'; sampleRate: 8000; payload: string } }
  | { event: 'end'; reason: 'user' };

export interface VocalLabsWebCallStats {
  receivedChunks: number;
  playedChunks: number;
  sentChunks: number;
  droppedSendPackets: number;
}

/**
 * Browser-compatible subset of the `vocallabsai-sdk` behavior.
 *
 * Implements the same WebSocket message contract observed in the upstream SDK:
 * - send `start`, `hangup_source`, then `media` packets
 * - receive `playAudio` packets
 *
 * This is intentionally minimal and tailored for Angular web apps.
 */
@Injectable({ providedIn: 'root' })
export class VocalLabsWebCallService {
  private ws: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private micSource: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private gainNode: GainNode | null = null;
  private mediaDest: MediaStreamAudioDestinationNode | null = null;

  private readonly targetSampleRate = 8000;
  private readonly samplesPerPacket = 160; // 20ms @ 8kHz

  private muted = false;
  private volume = 1.0;

  private receivedChunks = 0;
  private playedChunks = 0;
  private sentChunks = 0;
  private droppedSendPackets = 0;

  private nextPlayTime = 0;

  private onConnectedCb: (() => void) | null = null;
  private onDisconnectedCb: (() => void) | null = null;
  private onErrorCb: ((err: any) => void) | null = null;
  private onTranscriptCb: ((payload: { transcript: string; is_final: boolean; speaker?: string }) => void) | null = null;

  onConnected(cb: () => void) {
    this.onConnectedCb = cb;
  }
  onDisconnected(cb: () => void) {
    this.onDisconnectedCb = cb;
  }
  onError(cb: (err: any) => void) {
    this.onErrorCb = cb;
  }
  onTranscript(cb: (payload: { transcript: string; is_final: boolean; speaker?: string }) => void) {
    this.onTranscriptCb = cb;
  }

  getOutputMediaStream(): MediaStream | null {
    return this.mediaDest ? this.mediaDest.stream : null;
  }

  isConnected(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  isMuted(): boolean {
    return this.muted;
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    return this.muted;
  }

  setVolume(v: number) {
    const next = Math.max(0, Math.min(1, v));
    this.volume = next;
    if (this.gainNode) this.gainNode.gain.value = next;
  }

  getStats(): VocalLabsWebCallStats {
    return {
      receivedChunks: this.receivedChunks,
      playedChunks: this.playedChunks,
      sentChunks: this.sentChunks,
      droppedSendPackets: this.droppedSendPackets,
    };
  }

  async connect(wsUrl: string, connectionTimeoutMs = 8000): Promise<void> {
    await this.disconnect();

    try {
      await this.ensureAudioGraph();
      await this.ensureMicCapture();

      return new Promise<void>((resolve, reject) => {
        let outcome: 'pending' | 'ok' | 'fail' = 'pending';
        const timer = window.setTimeout(() => {
          if (outcome !== 'pending') return;
          outcome = 'fail';
          try {
            if (this.ws) {
              this.ws.onopen = null;
              this.ws.onmessage = null;
              this.ws.onerror = null;
              this.ws.onclose = null;
              this.ws.close();
            }
          } catch (e) {
            // ignore
          }
          this.ws = null;
          const err = new Error('VocalLabs connection timed out');
          this.onErrorCb && this.onErrorCb(err);
          reject(err);
        }, connectionTimeoutMs);

        const attachMessageHandler = () => {
          if (!this.ws) return;
          this.ws.onmessage = (event: MessageEvent) => {
            try {
              const msg = JSON.parse(String(event.data)) as VocalLabsIncomingMessage;
              if (!msg || !msg.event) return;
              if (msg.event === 'playAudio' && msg.media && msg.media.payload) {
                this.receivedChunks++;
                this.playIncomingChunk(msg.media.payload, msg.media.sampleRate || this.targetSampleRate);
                return;
              }
              if (msg.event === 'transcript') {
                const m = msg as any;
                if (this.onTranscriptCb && m.transcript != null) {
                  this.onTranscriptCb({
                    transcript: String(m.transcript),
                    is_final: !!m.is_final,
                    speaker: m.speaker != null ? String(m.speaker) : undefined,
                  });
                }
              }
            } catch (e) {
              this.onErrorCb && this.onErrorCb(e);
            }
          };
        };

        this.ws = new WebSocket(wsUrl);
        attachMessageHandler();

        this.ws.onopen = () => {
          if (outcome !== 'pending') return;
          try {
            this.send({ event: 'start', start: { streamId: 'inbound', mediaFormat: { Encoding: 'audio/x-l16', sampleRate: 8000 } } });
            this.send({ event: 'hangup_source', source: 'in_progress' });
            outcome = 'ok';
            window.clearTimeout(timer);
            this.onConnectedCb && this.onConnectedCb();
            resolve();
          } catch (e) {
            outcome = 'fail';
            window.clearTimeout(timer);
            this.onErrorCb && this.onErrorCb(e);
            reject(e instanceof Error ? e : new Error(String(e)));
          }
        };

        this.ws.onerror = () => {
          if (outcome !== 'pending') return;
          outcome = 'fail';
          window.clearTimeout(timer);
          const err = new Error('WebSocket connection failed');
          this.onErrorCb && this.onErrorCb(err);
          reject(err);
        };

        this.ws.onclose = () => {
          window.clearTimeout(timer);
          if (outcome === 'pending') {
            outcome = 'fail';
            reject(new Error('WebSocket closed before connection completed'));
            return;
          }
          if (outcome === 'ok') {
            this.onDisconnectedCb && this.onDisconnectedCb();
          }
        };
      });
    } catch (e) {
      this.onErrorCb && this.onErrorCb(e);
      throw e;
    }
  }

  async disconnect(): Promise<void> {
    // stop mic capture first to stop sending while socket is closing
    this.stopProcessor();

    if (this.ws) {
      try {
        if (this.ws.readyState === WebSocket.OPEN) {
          try {
            this.send({ event: 'end', reason: 'user' });
          } catch (e) {
            // ignore
          }
        }
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        // Clear before close so we don't double-fire disconnect callbacks from the connect() promise handler.
        this.ws.onclose = null;
        this.ws.close();
      } catch (e) {
        // ignore
      } finally {
        this.ws = null;
      }
    }

    if (this.mediaStream) {
      try {
        this.mediaStream.getTracks().forEach((t) => t.stop());
      } catch (e) {}
      this.mediaStream = null;
    }

    // keep audio context alive for reuse; callers can refresh by reloading dialog
    this.onDisconnectedCb && this.onDisconnectedCb();
  }

  async dispose(): Promise<void> {
    await this.disconnect();
    try {
      if (this.audioContext) {
        await this.audioContext.close();
      }
    } catch (e) {}

    this.audioContext = null;
    this.micSource = null;
    this.processor = null;
    this.gainNode = null;
    this.mediaDest = null;
  }

  private send(message: VocalLabsOutgoingMessage) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(message));
  }

  private async ensureAudioGraph(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    if (!this.gainNode) {
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = this.volume;
    }

    if (!this.mediaDest) {
      this.mediaDest = this.audioContext.createMediaStreamDestination();
    }

    // Connect playback chain once
    try {
      this.gainNode.disconnect();
    } catch (e) {}
    this.gainNode.connect(this.mediaDest);
    // Also route to speakers (MediaStreamDestination alone is silent until consumed).
    try {
      this.gainNode.connect(this.audioContext.destination);
    } catch (e) {
      // ignore
    }

    this.nextPlayTime = this.audioContext.currentTime;
  }

  private async ensureMicCapture(): Promise<void> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('Microphone capture not available in this browser');
    }

    if (!this.mediaStream) {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
    }

    if (!this.audioContext) {
      await this.ensureAudioGraph();
    }

    if (!this.micSource) {
      this.micSource = this.audioContext!.createMediaStreamSource(this.mediaStream);
    }

    // ScriptProcessor is deprecated but still the most compatible choice for Angular 8 era browsers.
    // We keep buffer modest; we resample to 8k and packetize to 20ms frames.
    if (!this.processor) {
      this.processor = this.audioContext!.createScriptProcessor(2048, 1, 1);
      this.processor.onaudioprocess = (ev: AudioProcessingEvent) => {
        this.handleMicAudio(ev.inputBuffer.getChannelData(0), this.audioContext!.sampleRate);
      };
      this.micSource.connect(this.processor);
      // Connect to destination with zero gain to keep processor alive without echoing mic.
      const silentGain = this.audioContext!.createGain();
      silentGain.gain.value = 0;
      this.processor.connect(silentGain);
      silentGain.connect(this.audioContext!.destination);
    }
  }

  private stopProcessor() {
    if (this.processor) {
      try {
        this.processor.disconnect();
      } catch (e) {}
      this.processor.onaudioprocess = null;
      this.processor = null;
    }
    if (this.micSource) {
      try {
        this.micSource.disconnect();
      } catch (e) {}
      this.micSource = null;
    }
  }

  private micCarry: Float32Array = new Float32Array(0);

  private handleMicAudio(input: Float32Array, inputRate: number) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (this.muted) return;

    const resampled = this.resampleLinear(input, inputRate, this.targetSampleRate);

    // append to carry, then emit packets of 160 samples
    const combined = new Float32Array(this.micCarry.length + resampled.length);
    combined.set(this.micCarry, 0);
    combined.set(resampled, this.micCarry.length);

    let offset = 0;
    while (offset + this.samplesPerPacket <= combined.length) {
      const frame = combined.subarray(offset, offset + this.samplesPerPacket);
      const int16 = this.float32ToInt16(frame);
      const payload = this.int16ToBase64(int16);
      if (payload) {
        try {
          this.send({ event: 'media', media: { contentType: 'audio/x-l16', sampleRate: 8000, payload } });
          this.sentChunks++;
        } catch (e) {
          this.droppedSendPackets++;
        }
      }
      offset += this.samplesPerPacket;
    }

    this.micCarry = combined.subarray(offset);
  }

  private playIncomingChunk(base64: string, sampleRate: number) {
    if (!this.audioContext || !this.gainNode) return;

    const pcm16 = this.base64ToInt16(base64);
    if (!pcm16 || pcm16.length === 0) return;

    // Normalize to float32
    const float = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) float[i] = pcm16[i] / 32768;

    const floatAtOutRate = sampleRate === this.audioContext.sampleRate
      ? float
      : this.resampleLinear(float, sampleRate, this.audioContext.sampleRate);

    const buffer = this.audioContext.createBuffer(1, floatAtOutRate.length, this.audioContext.sampleRate);
    buffer.getChannelData(0).set(floatAtOutRate);

    const src = this.audioContext.createBufferSource();
    src.buffer = buffer;
    src.connect(this.gainNode);

    const now = this.audioContext.currentTime;
    if (this.nextPlayTime < now) this.nextPlayTime = now;
    src.start(this.nextPlayTime);
    this.nextPlayTime += buffer.duration;
    this.playedChunks++;
  }

  private resampleLinear(input: Float32Array, fromRate: number, toRate: number): Float32Array {
    if (fromRate === toRate) return input;
    if (!input || input.length === 0) return new Float32Array(0);

    const ratio = fromRate / toRate;
    const newLen = Math.max(1, Math.floor(input.length / ratio));
    const output = new Float32Array(newLen);
    for (let i = 0; i < newLen; i++) {
      const srcIndex = i * ratio;
      const low = Math.floor(srcIndex);
      const high = Math.min(low + 1, input.length - 1);
      const t = srcIndex - low;
      output[i] = input[low] * (1 - t) + input[high] * t;
    }
    return output;
  }

  private float32ToInt16(input: Float32Array): Int16Array {
    const out = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]));
      out[i] = s < 0 ? s * 32768 : s * 32767;
    }
    return out;
  }

  private int16ToBase64(samples: Int16Array): string {
    // Encode to binary string then btoa.
    // Use little-endian to match common L16 PCM; the upstream SDK handles endianness internally.
    let binary = '';
    for (let i = 0; i < samples.length; i++) {
      const v = samples[i];
      const lo = v & 0xff;
      const hi = (v >> 8) & 0xff;
      binary += String.fromCharCode(lo, hi);
    }
    return btoa(binary);
  }

  private base64ToInt16(base64: string): Int16Array {
    const bin = atob(base64);
    const len = Math.floor(bin.length / 2);
    const out = new Int16Array(len);
    for (let i = 0; i < len; i++) {
      const lo = bin.charCodeAt(i * 2);
      const hi = bin.charCodeAt(i * 2 + 1);
      // little-endian
      const val = (hi << 8) | lo;
      out[i] = val >= 0x8000 ? val - 0x10000 : val;
    }
    return out;
  }
}

